import { supabase } from './supabase';
import type { OnboardingLead } from './onboardingLeads';

/**
 * Service des demandes d'inscription (onboarding YumLink) — accès Supabase.
 * Lecture/rejet via RLS (réservé super_admin, cf. sql/30_onboarding_leads.sql).
 * Validation via l'Edge Function sécurisée `approve-lead` (crée le site + email).
 * Logique pure dans onboardingLeads.ts (testée à part).
 */

export interface LoadLeadsResult {
  ok: boolean;
  leads: OnboardingLead[];
  error?: string;
}

/** Charge toutes les demandes (les plus récentes d'abord). */
export async function loadLeads(): Promise<LoadLeadsResult> {
  const { data, error } = await supabase
    .from('onboarding_leads')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) return { ok: false, leads: [], error: error.message };
  return { ok: true, leads: (data ?? []) as OnboardingLead[] };
}

export interface MutationResult {
  ok: boolean;
  error?: string;
}

/** Rejette une demande (test/spam) → statut 'rejete'. */
export async function rejectLead(id: string): Promise<MutationResult> {
  const { error } = await supabase
    .from('onboarding_leads')
    .update({ status: 'rejete' })
    .eq('id', id);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export interface ApproveResult {
  ok: boolean;
  tenantId?: string;
  sitePath?: string;
  adminPath?: string;
  email?: string;
  tempPassword?: string;
  emailSent?: boolean;
  error?: string;
}

/**
 * Valide une demande : appelle l'Edge Function `approve-lead` (le JWT du super
 * admin connecté est transmis automatiquement) qui crée le site via `provision`
 * et envoie l'email de bienvenue, puis marque la demande « créée ».
 */
export async function approveLead(id: string, slug: string): Promise<ApproveResult> {
  const { data, error } = await supabase.functions.invoke('approve-lead', {
    body: { lead_id: id, slug },
  });
  if (error) return { ok: false, error: error.message };
  const res = (data ?? {}) as ApproveResult;
  if (!res.ok) return { ok: false, error: res.error ?? 'Validation impossible.' };
  return res;
}
